import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { DollarSign, FileDown, Trash2, Play, Loader2, Plus, Pencil, Eye, Receipt, TrendingUp, TrendingDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtBRL = (v) => v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const PjPagamentosTab = () => {
  const now = new Date();
  const [ano, setAno] = useState(String(now.getFullYear()));
  const [mes, setMes] = useState(String(now.getMonth() + 1).padStart(2, '0'));

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({ valor: '', descricao: '', data_pagamento: '', observacoes: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [uploadingNf, setUploadingNf] = useState(false);

  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [itemDialog, setItemDialog] = useState(null);
  const [itemForm, setItemForm] = useState({ descricao: '', tipo: 'provento', valor: '' });
  const [itemEditId, setItemEditId] = useState(null);
  const [savingItem, setSavingItem] = useState(false);

  const [showPagamentoDialog, setShowPagamentoDialog] = useState(false);
  const [pagamentoData, setPagamentoData] = useState('');

  const [showAdiantamentoDialog, setShowAdiantamentoDialog] = useState(false);
  const [adiantamentoPct, setAdiantamentoPct] = useState(0.4);
  const [generatingAdiantamento, setGeneratingAdiantamento] = useState(false);
  const [showConfirmAdiantamentoDialog, setShowConfirmAdiantamentoDialog] = useState(false);
  const [confirmAdiantamentoData, setConfirmAdiantamentoData] = useState({ count: 0, pctDisplay: '' });

  const [tipoFiltro, setTipoFiltro] = useState('todos');
  const [empresas, setEmpresas] = useState({});
  const [nfComp, setNfComp] = useState({});  // employee_id → { id, nome }

  const competencia = `${ano}-${mes}`;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ano: parseInt(ano), mes: parseInt(mes) };
      if (tipoFiltro !== 'todos') params.tipo = tipoFiltro;
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/api/pj-pagamentos`, { ...authHeader(), params }),
        axios.get(`${API}/api/pj-pagamentos/nf-competencia/list`, { ...authHeader(), params: { competencia: `${ano}-${mes}` } }),
      ]);
      setPayments(r1.data || []);

      const map = {};
      (Array.isArray(r2.data) ? r2.data : []).forEach((nf) => {
        map[nf.employee_id] = { id: nf.id, nome: nf.nf_arquivo_nome || 'NF' };
      });
      setNfComp(map);
    } catch { toast.error('Erro ao carregar pagamentos'); }
    finally { setLoading(false); }
  }, [ano, mes, tipoFiltro]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    axios.get(`${API}/api/companies`, authHeader())
      .then((r) => {
        const map = {};
        (Array.isArray(r.data) ? r.data : []).forEach((c) => {
          map[c.id] = c.razao_social || c.name || c.id;
        });
        setEmpresas(map);
      })
      .catch(() => {});
  }, []);

  const totalPagar = payments.reduce((s, p) => s + (p.valor || 0), 0);

  // Group payments by empresa → colaborador
  const grouped = payments.reduce((acc, p) => {
    const empKey = p.empresa_id || '__sem_empresa__';
    if (!acc[empKey]) acc[empKey] = { nome: empresas[p.empresa_id] || p.empresa_nome || 'Sem empresa', employees: {} };
    if (!acc[empKey].employees[p.employee_id]) acc[empKey].employees[p.employee_id] = { nome: p.employee_nome || 'Desconhecido', payments: [] };
    acc[empKey].employees[p.employee_id].payments.push(p);
    return acc;
  }, {});
  const grupos = Object.entries(grouped)
    .sort((a, b) => a[1].nome.localeCompare(b[1].nome))
    .map(([key, g]) => ({
      key,
      nome: g.nome,
      totalEmpresa: Object.values(g.employees).reduce((s, e) => s + e.payments.reduce((s2, p) => s2 + (p.valor || 0), 0), 0),
      totalRegistros: Object.values(g.employees).reduce((s, e) => s + e.payments.length, 0),
      employeesList: Object.entries(g.employees)
        .sort((a, b) => a[1].nome.localeCompare(b[1].nome))
        .map(([eid, emp]) => {
          const nfc = nfComp[eid];
          return {
            id: eid,
            nome: emp.nome,
            nfCompetencia: nfc?.id || null,
            nfCompetenciaNome: nfc?.nome || null,
            totalColab: emp.payments.reduce((s, p) => s + (p.valor || 0), 0),
            payments: emp.payments,
          };
        }),
    }));

  const gerarPagamentos = async (dataPagamento) => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/api/pj-pagamentos/gerar-lote`, { competencia, data_pagamento: dataPagamento || null }, authHeader());
      const partes = [];
      if (r.data.gerados) partes.push(`${r.data.gerados} criado(s)`);
      if (r.data.atualizados) partes.push(`${r.data.atualizados} atualizado(s)`);
      toast.success(partes.join(' · ') || 'Nenhuma alteração');
      if (r.data.ignorados?.length) toast.info(`${r.data.ignorados.length} ignorado(s)`);
      setShowPagamentoDialog(false);
      loadData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao gerar'); }
    finally { setGenerating(false); }
  };

  const executarGerarAdiantamentos = async () => {
    setGeneratingAdiantamento(true);
    try {
      const r = await axios.post(`${API}/api/pj-pagamentos/gerar-adiantamentos`, {
        competencia, percentual: adiantamentoPct,
      }, authHeader());
      const msg = [`${r.data.gerados} adiantamento(s) gerado(s)`];
      if (r.data.atualizados) msg.push(`${r.data.atualizados} atualizado(s)`);
      toast.success(msg.join(', '));
      if (r.data.ignorados?.length) toast.info(`${r.data.ignorados.length} ignorado(s)`);
      setShowAdiantamentoDialog(false);
      loadData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao gerar adiantamentos'); }
    finally { setGeneratingAdiantamento(false); }
  };

  const confirmarGerarAdiantamentos = async () => {
    setShowConfirmAdiantamentoDialog(false);
    await executarGerarAdiantamentos();
  };

  const abrirDialogAdiantamento = async () => {
    try {
      const r = await axios.get(`${API}/api/salary-advances`, authHeader());
      const advances = Array.isArray(r.data) ? r.data : [];
      const pj = advances.find(a => a.percentage_pj != null);
      if (pj) setAdiantamentoPct(pj.percentage_pj / 100);
    } catch {}
    setShowAdiantamentoDialog(true);
  };

  const gerarAdiantamentos = async () => {
    try {
      const check = await axios.get(`${API}/api/pj-pagamentos`, {
        ...authHeader(),
        params: { ano: parseInt(ano), mes: parseInt(mes), tipo: 'adiantamento' }
      });
      const existing = Array.isArray(check.data) ? check.data : [];
      if (existing.length > 0) {
        const pctDisplay = (adiantamentoPct * 100).toFixed(0);
        setConfirmAdiantamentoData({ count: existing.length, pctDisplay });
        setShowConfirmAdiantamentoDialog(true);
        return;
      }
      await executarGerarAdiantamentos();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao verificar adiantamentos'); }
  };

  const deletePayment = async (payId, nome) => {
    if (!window.confirm(`Excluir pagamento de ${nome}?`)) return;
    try {
      await axios.delete(`${API}/api/pj-pagamentos/${payId}`, authHeader());
      toast.success('Excluído');
      if (detailData?.id === payId) setDetailData(null);
      loadData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  const openEdit = (pay) => {
    setEditDialog(pay);
    setEditForm({
      valor: pay.valor || 0,
      descricao: pay.descricao || '',
      data_pagamento: pay.data_pagamento || '',
      observacoes: pay.observacoes || '',
    });
  };

  const saveEdit = async () => {
    if (!editDialog) return;
    const v = editForm.valor;
    if (!v || v <= 0) return toast.error('Valor inválido');
    setSavingEdit(true);
    try {
      await axios.put(`${API}/api/pj-pagamentos/${editDialog.id}`, {
        valor: v, descricao: editForm.descricao,
        data_pagamento: editForm.data_pagamento || null, observacoes: editForm.observacoes || null,
      }, authHeader());
      toast.success('Atualizado');
      setEditDialog(null);
      loadData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
    finally { setSavingEdit(false); }
  };

  const openDetail = async (payId) => {
    setLoadingDetail(true);
    try {
      const r = await axios.get(`${API}/api/pj-pagamentos/${payId}`, authHeader());
      setDetailData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao carregar detalhe'); }
    finally { setLoadingDetail(false); }
  };

  const openAddItem = (tipo) => {
    setItemEditId(null);
    setItemForm({ descricao: '', tipo, valor: 0 });
    setItemDialog(true);
  };

  const openEditItem = (item) => {
    setItemEditId(item.id);
    setItemForm({ descricao: item.descricao, tipo: item.tipo, valor: item.valor });
    setItemDialog(true);
  };

  const saveItem = async () => {
    if (!itemForm.descricao || !itemForm.valor) return toast.error('Descrição e valor obrigatórios');
    const v = itemForm.valor;
    if (v <= 0) return toast.error('Valor inválido');
    setSavingItem(true);
    try {
      const url = itemEditId
        ? `${API}/api/pj-pagamentos/${detailData.id}/itens/${itemEditId}`
        : `${API}/api/pj-pagamentos/${detailData.id}/itens`;
      const method = itemEditId ? axios.put : axios.post;
      const r = await method(url, { descricao: itemForm.descricao, tipo: itemForm.tipo, valor: v }, authHeader());
      setDetailData((prev) => ({
        ...prev,
        itens: itemEditId
          ? prev.itens.map((it) => it.id === itemEditId ? { ...it, descricao: itemForm.descricao, tipo: itemForm.tipo, valor: v } : it)
          : [...prev.itens, { id: r.data.id || Date.now(), descricao: itemForm.descricao, tipo: itemForm.tipo, valor: v }],
        total_proventos: r.data.total_proventos,
        total_descontos: r.data.total_descontos,
        valor: r.data.valor_liquido,
      }));
      setItemDialog(false);
      toast.success(itemEditId ? 'Item atualizado' : 'Item adicionado');
      loadData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
    finally { setSavingItem(false); }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Remover este item?')) return;
    try {
      const r = await axios.delete(`${API}/api/pj-pagamentos/${detailData.id}/itens/${itemId}`, authHeader());
      setDetailData((prev) => ({
        ...prev,
        itens: prev.itens.filter((it) => it.id !== itemId),
        total_proventos: r.data.total_proventos,
        total_descontos: r.data.total_descontos,
        valor: r.data.valor_liquido,
      }));
      toast.success('Item removido');
      loadData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  const [baixandoLote, setBaixandoLote] = useState(false);

  const downloadRecibo = async (payId, nome) => {
    try {
      const r = await axios.post(`${API}/api/pj-pagamentos/${payId}/recibo`, {}, {
        ...authHeader(), responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `recibo_pj_${nome}_${competencia}.pdf`.replace(/\s/g, '_');
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao gerar recibo'); }
  };

  const downloadRecibosLote = async (empresaId) => {
    if (payments.length === 0) return toast.error('Nenhum pagamento para emitir recibos');
    setBaixandoLote(true);
    try {
      const body = { competencia };
      if (empresaId) body.empresa_id = empresaId;
      const r = await axios.post(`${API}/api/pj-pagamentos/recibos/lote`, body, {
        ...authHeader(), responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      const suffix = empresaId ? `_${empresaId}` : '';
      a.href = url; a.download = `recibos_pj_${competencia}${suffix}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Recibos baixados');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao baixar recibos'); }
    finally { setBaixandoLote(false); }
  };

  return (
    <div className="space-y-4">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <DollarSign className="h-6 w-6 text-indigo-600" />
          <div>
            <h3 className="text-lg font-semibold">Pagamentos PJ — {MESES[parseInt(mes) - 1]}/{ano}</h3>
            <p className="text-xs text-slate-500">{payments.length} pagamento(s) · {fmtBRL(totalPagar)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={ano} onValueChange={setAno}>
            <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map((a) => (
                <SelectItem key={a} value={String(a)}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={mes} onValueChange={setMes}>
            <SelectTrigger className="w-24 h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MESES.map((m, i) => (
                <SelectItem key={i + 1} value={String(i + 1).padStart(2, '0')}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tipo filter + Totais + Gerar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {[
            { v: 'todos', l: 'Todos' },
            { v: 'fixo', l: 'Pagamentos' },
            { v: 'adiantamento', l: 'Adiantamentos' },
          ].map((t) => (
            <button
              key={t.v}
              onClick={() => setTipoFiltro(t.v)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                tipoFiltro === t.v
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => downloadRecibosLote()} disabled={baixandoLote || payments.length === 0} size="sm">
            {baixandoLote ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Gerando...</> : <><FileDown className="h-4 w-4 mr-1" /> Baixar Todos</>}
          </Button>
          {tipoFiltro !== 'adiantamento' && (
            <Button onClick={() => { setPagamentoData(''); setShowPagamentoDialog(true); }} disabled={generating} size="sm">
              {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Gerando...</> : <><Play className="h-4 w-4 mr-1" /> Gerar Pagamentos</>}
            </Button>
          )}
          {tipoFiltro !== 'fixo' && (
            <Button variant="outline" onClick={abrirDialogAdiantamento} disabled={generatingAdiantamento} size="sm">
              {generatingAdiantamento ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Gerando...</> : <><DollarSign className="h-4 w-4 mr-1" /> Gerar Adiantamentos</>}
            </Button>
          )}
        </div>
      </div>

      {/* Cards de total */}
      <div className="grid grid-cols-2 gap-2">
        <Card><CardContent className="p-3">
          <div className="text-xs text-slate-500 uppercase">
            {tipoFiltro === 'adiantamento' ? 'Adiantamentos' : tipoFiltro === 'fixo' ? 'Pagamentos' : 'Registros'}
          </div>
          <div className="text-xl font-bold">{payments.length}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-xs text-indigo-700 uppercase">Total a Pagar</div>
          <div className="text-xl font-bold text-indigo-700">{fmtBRL(totalPagar)}</div>
        </CardContent></Card>
      </div>

      {/* Tabela agrupada por empresa */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-center"><Loader2 className="h-5 w-5 inline animate-spin" /></div>
          ) : payments.length === 0 ? (
            <div className="p-6 text-center text-slate-500">
              {tipoFiltro === 'adiantamento'
                ? `Nenhum adiantamento para ${MESES[parseInt(mes) - 1]}/${ano}.`
                : tipoFiltro === 'fixo'
                ? `Nenhum pagamento fixo para ${MESES[parseInt(mes) - 1]}/${ano}.`
                : `Nenhum pagamento PJ para ${MESES[parseInt(mes) - 1]}/${ano}.`}
            </div>
          ) : (
            <div className="divide-y">
              {grupos.map((grupo) => (
                <div key={grupo.key}>
                  {/* Cabeçalho da empresa */}
                  <div className="flex items-center gap-2 px-4 py-2 bg-indigo-50 border-b border-indigo-100">
                    <div className="text-xs font-semibold text-indigo-800 uppercase tracking-wider">
                      {grupo.nome}
                    </div>
                    <Badge variant="secondary" className="text-[10px]">
                      {grupo.totalRegistros} registro(s)
                    </Badge>
                    <span className="text-xs text-indigo-600 ml-auto font-semibold">
                      Total: {fmtBRL(grupo.totalEmpresa)}
                    </span>
                    <Button size="sm" variant="ghost" className="h-6 text-indigo-600 hover:text-indigo-800"
                      onClick={() => downloadRecibosLote(grupo.key === '__sem_empresa__' ? null : grupo.key)}
                      disabled={baixandoLote} title="Baixar recibos desta empresa">
                      <FileDown className="h-3.5 w-3.5 mr-1" /> ZIP
                    </Button>
                  </div>

                  {/* Colaboradores — tree indent */}
                  <div className="pl-8 relative">
                    {/* vertical line from first to last collaborator */}
                    <div className="absolute left-[1.125rem] top-0 bottom-0 w-px bg-indigo-200" />
                    {grupo.employeesList.map((colab, ci) => (
                      <div key={colab.id} className="relative">
                        {/* connector dot + line */}
                        <div className="absolute -left-6 top-3 flex items-center">
                          <div className="w-3 h-px bg-indigo-200 flex-shrink-0" />
                          <div className="w-2 h-2 rounded-full border-2 border-indigo-400 bg-white flex-shrink-0" />
                        </div>
                        {/* Cabeçalho do colaborador */}
                        <div className="flex items-center gap-2 pl-4 pr-3 py-2 bg-slate-50 border-b border-slate-200 rounded-t ml-2">
                          <div className="text-sm font-semibold text-slate-800">{colab.nome}</div>
                          <Badge variant="outline" className="text-[10px] text-slate-500 font-normal">
                            {colab.payments.length} registro(s)
                          </Badge>
                          <div className="flex items-center gap-1">
                            <input
                              type="file" accept=".pdf,.png,.jpg,.jpeg" id={`nf-upload-${colab.id}`}
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                try {
                                  const fd = new FormData();
                                  fd.append('employee_id', colab.id);
                                  fd.append('competencia', competencia);
                                  fd.append('arquivo', file);
                                  await axios.post(`${API}/api/pj-pagamentos/nf-competencia`, fd, authHeader());
                                  toast.success('NF da competência anexada');
                                  loadData();
                                } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao anexar NF'); }
                                e.target.value = '';
                              }}
                            />
                            <Button size="sm" variant="ghost" className="h-5 text-[10px] text-slate-500 px-1"
                              onClick={() => document.getElementById(`nf-upload-${colab.id}`).click()}
                              title="Anexar NF da competência">
                              <FileDown className="h-3 w-3 mr-1" /> +NF
                            </Button>
                            {colab.nfCompetencia && (
                              <Button size="sm" variant="ghost" className="h-5 text-[10px] text-emerald-600 px-1"
                                onClick={async () => {
                                  try {
                                    const r = await axios.get(`${API}/api/pj-pagamentos/nf-competencia/${colab.nfCompetencia}`,
                                      { ...authHeader(), responseType: 'blob' });
                                    const url = URL.createObjectURL(new Blob([r.data]));
                                    const a = document.createElement('a');
                                    a.href = url; a.download = colab.nfCompetenciaNome || 'nf_competencia.pdf';
                                    a.click(); URL.revokeObjectURL(url);
                                  } catch (err) { toast.error('Erro ao baixar NF'); }
                                }}>
                                <FileDown className="h-3 w-3 mr-1" /> NF
                              </Button>
                            )}
                          </div>
                          <span className="text-xs text-slate-500 ml-auto font-medium">
                            Total: {fmtBRL(colab.totalColab)}
                          </span>
                        </div>
                        {/* Tabela de pagamentos do colaborador */}
                        <table className="w-full text-sm ml-2" style={{ width: 'calc(100% - 0.5rem)' }}>
                          <thead className="bg-white text-[10px] uppercase text-slate-400">
                            <tr>
                              <th className="text-left p-1.5 pl-4 w-28">Tipo</th>
                              <th className="text-right p-1.5 w-24">Valor</th>
                              <th className="text-left p-1.5 w-28">Data Pagto.</th>
                              <th className="text-left p-1.5 w-24">NF</th>
                              <th className="text-right p-1.5">Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {colab.payments.map((pay) => (
                              <tr key={pay.id} className="border-t hover:bg-slate-50">
                                <td className="p-1.5 pl-4">
                                  <Badge variant={pay.tipo === 'fixo' ? 'default' : pay.tipo === 'adiantamento' ? 'secondary' : 'outline'} className="text-[10px]">
                                    {pay.tipo === 'adiantamento' ? 'Adiantamento' : pay.tipo === 'fixo' ? 'Fixo' : pay.tipo}
                                  </Badge>
                                </td>
                                <td className="p-1.5 text-right font-semibold text-indigo-700">{fmtBRL(pay.valor)}</td>
                                <td className="p-1.5 text-[11px]">{pay.data_pagamento || '—'}</td>
                                <td className="p-1.5 text-[11px]">
                                  {pay.nf_arquivo_nome ? (
                                    <Button size="sm" variant="ghost" className="h-5 text-[10px] text-indigo-600"
                                      onClick={async () => {
                                        try {
                                          const r = await axios.get(`${API}/api/pj-pagamentos/${pay.id}/nf`,
                                            { ...authHeader(), responseType: 'blob' });
                                          const url = URL.createObjectURL(new Blob([r.data]));
                                          const a = document.createElement('a');
                                          a.href = url; a.download = pay.nf_arquivo_nome;
                                          a.click(); URL.revokeObjectURL(url);
                                        } catch (err) { toast.error('Erro ao baixar NF'); }
                                      }}>
                                      <FileDown className="h-3 w-3 mr-1" /> NF
                                    </Button>
                                  ) : '—'}
                                </td>
                                <td className="p-1.5 text-right whitespace-nowrap">
                                  <Button size="sm" variant="ghost" className="h-6 w-6" onClick={() => openDetail(pay.id)} title="Detalhamento">
                                    <Receipt className="h-3 w-3 text-indigo-600" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-6 w-6" onClick={() => openEdit(pay)} title="Editar">
                                    <Pencil className="h-3 w-3 text-slate-600" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-6 w-6" onClick={() => downloadRecibo(pay.id, pay.employee_nome)} title="Recibo PDF">
                                    <FileDown className="h-3 w-3 text-emerald-600" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-6 w-6" onClick={() => deletePayment(pay.id, pay.employee_nome)} title="Excluir">
                                    <Trash2 className="h-3 w-3 text-red-600" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog: Detalhamento */}
      <Dialog open={!!detailData} onOpenChange={(o) => { if (!o) setDetailData(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhamento — {detailData?.employee_nome}</DialogTitle>
            <DialogDescription>
              {detailData?.competencia} · {detailData?.employee_cnpj || detailData?.employee_cpf || ''}
            </DialogDescription>
          </DialogHeader>
          {loadingDetail ? (
            <div className="p-6 text-center"><Loader2 className="h-5 w-5 inline animate-spin" /></div>
          ) : detailData && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-slate-500">Contratante:</span> {empresas[detailData.empresa_id] || detailData.empresa_nome || detailData.empresa_id || '—'}</div>
                <div><span className="text-slate-500">NF Pagto:</span> {detailData.nf_arquivo_nome ? (
                  <Button size="sm" variant="link" className="h-auto p-0 text-indigo-600"
                    onClick={async () => {
                      try {
                        const r = await axios.get(`${API}/api/pj-pagamentos/${detailData.id}/nf`,
                          { ...authHeader(), responseType: 'blob' });
                        const url = URL.createObjectURL(new Blob([r.data]));
                        const a = document.createElement('a');
                        a.href = url; a.download = detailData.nf_arquivo_nome;
                        a.click(); URL.revokeObjectURL(url);
                      } catch (err) { toast.error('Erro ao baixar NF'); }
                    }}>
                    <FileDown className="h-3 w-3 mr-1" /> {detailData.nf_arquivo_nome}
                  </Button>
                ) : '—'}</div>
                <div><span className="text-slate-500">NF Competência:</span> {nfComp[detailData?.employee_id] ? (
                  <Button size="sm" variant="link" className="h-auto p-0 text-emerald-600"
                    onClick={async () => {
                      try {
                        const nf = nfComp[detailData.employee_id];
                        const r = await axios.get(`${API}/api/pj-pagamentos/nf-competencia/${nf.id}`,
                          { ...authHeader(), responseType: 'blob' });
                        const url = URL.createObjectURL(new Blob([r.data]));
                        const a = document.createElement('a');
                        a.href = url; a.download = nf.nome;
                        a.click(); URL.revokeObjectURL(url);
                      } catch (err) { toast.error('Erro ao baixar NF'); }
                    }}>
                    <FileDown className="h-3 w-3 mr-1" /> {nfComp[detailData.employee_id].nome}
                  </Button>
                ) : '—'}</div>
                <div><span className="text-slate-500">Data Pagto.:</span> {detailData.data_pagamento || '—'}</div>
                <div><span className="text-slate-500">Observações:</span> {detailData.observacoes || '—'}</div>
              </div>

              {/* Memória de cálculo */}
              {detailData.memoria_calculo?.passos && (
                <div className="bg-slate-50 border rounded-md p-3 space-y-1.5">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Memória de Cálculo</div>
                  {detailData.memoria_calculo.passos.map((p, i) => (
                    <div key={i} className={`flex items-center justify-between text-sm ${
                      p.tipo === 'total' ? 'border-t pt-1.5 mt-1.5 font-bold text-indigo-700' : 'text-slate-600'
                    }`}>
                      <span>{p.descricao}</span>
                      <span className={p.tipo === 'desconto' ? 'text-red-600' : p.tipo === 'base' ? 'text-slate-700' : ''}>
                        {p.tipo === 'desconto' ? '- ' : p.tipo === 'total' ? '= ' : ''}{fmtBRL(p.valor)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => openAddItem('provento')} className="text-emerald-700 border-emerald-300">
                  <TrendingUp className="h-4 w-4 mr-1" /> Adicionar Provento
                </Button>
                <Button size="sm" variant="outline" onClick={() => openAddItem('desconto')} className="text-red-700 border-red-300">
                  <TrendingDown className="h-4 w-4 mr-1" /> Adicionar Desconto
                </Button>
              </div>

              <table className="w-full text-sm border rounded-md overflow-hidden">
                <thead className="bg-slate-100 text-xs uppercase">
                  <tr>
                    <th className="text-left p-2">Descrição</th>
                    <th className="text-left p-2">Tipo</th>
                    <th className="text-right p-2">Valor</th>
                    <th className="text-right p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(detailData.itens || []).map((it) => (
                    <tr key={it.id} className={`border-t ${it.tipo === 'desconto' ? 'bg-red-50/30' : ''}`}>
                      <td className="p-2">{it.descricao}</td>
                      <td className="p-2">
                        <Badge variant={it.tipo === 'provento' ? 'default' : 'destructive'} className="text-xs">
                          {it.tipo === 'provento' ? 'Provento' : 'Desconto'}
                        </Badge>
                      </td>
                      <td className={`p-2 text-right font-semibold ${it.tipo === 'provento' ? 'text-emerald-700' : 'text-red-700'}`}>
                        {fmtBRL(it.valor)}
                      </td>
                      <td className="p-2 text-right">
                        {it.id !== 'base' && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => openEditItem(it)} title="Editar item">
                              <Pencil className="h-3 w-3 text-slate-600" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteItem(it.id)} title="Remover item">
                              <Trash2 className="h-3 w-3 text-red-600" />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 font-semibold">
                  <tr>
                    <td colSpan={2} className="p-2 text-right">Total Proventos →</td>
                    <td className="p-2 text-right text-emerald-700">{fmtBRL(detailData.total_proventos)}</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td colSpan={2} className="p-2 text-right">Total Descontos →</td>
                    <td className="p-2 text-right text-red-700">{fmtBRL(detailData.total_descontos)}</td>
                    <td></td>
                  </tr>
                  <tr className="bg-indigo-50">
                    <td colSpan={2} className="p-2 text-right text-base">LÍQUDO A RECEBER →</td>
                    <td className="p-2 text-right text-xl font-bold text-indigo-700">{fmtBRL(detailData.valor)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Editar pagamento */}
      <Dialog open={!!editDialog} onOpenChange={(o) => { if (!o) setEditDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Pagamento</DialogTitle>
            <DialogDescription>{editDialog?.employee_nome} — {competencia}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Valor *</Label>
              <CurrencyInput value={editForm.valor} onChange={(v) => setEditForm({ ...editForm, valor: v })} placeholder="0,00" />
            </div>
            <div>
              <Label>NF da Competência ({competencia})</Label>
              <div className="flex items-center gap-2">
                <Input type="file" accept=".pdf,.png,.jpg,.jpeg"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const fd = new FormData();
                      fd.append('employee_id', editDialog.employee_id);
                      fd.append('competencia', competencia);
                      fd.append('arquivo', file);
                      await axios.post(`${API}/api/pj-pagamentos/nf-competencia`, fd, authHeader());
                      toast.success('NF da competência anexada');
                      loadData();
                    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao anexar NF'); }
                    e.target.value = '';
                  }}
                />
                {nfComp[editDialog?.employee_id] && (
                  <Button size="sm" variant="ghost" onClick={async () => {
                    try {
                      const nf = nfComp[editDialog.employee_id];
                      const r = await axios.get(`${API}/api/pj-pagamentos/nf-competencia/${nf.id}`,
                        { ...authHeader(), responseType: 'blob' });
                      const url = URL.createObjectURL(new Blob([r.data]));
                      const a = document.createElement('a');
                      a.href = url; a.download = nf.nome;
                      a.click(); URL.revokeObjectURL(url);
                    } catch (err) { toast.error('Erro ao baixar NF'); }
                  }}>
                    <FileDown className="h-4 w-4 mr-1" /> {nfComp[editDialog.employee_id].nome}
                  </Button>
                )}
              </div>
            </div>
            <div>
              <Label>NF do Pagamento (específica)</Label>
              <div className="flex items-center gap-2">
                <Input type="file" accept=".pdf,.png,.jpg,.jpeg"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploadingNf(true);
                    try {
                      const fd = new FormData();
                      fd.append('arquivo', file);
                      await axios.post(`${API}/api/pj-pagamentos/${editDialog.id}/nf`, fd, authHeader());
                      toast.success('NF anexada');
                      loadData();
                    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao anexar NF'); }
                    finally { setUploadingNf(false); }
                  }}
                  disabled={uploadingNf}
                />
                {editDialog?.nf_arquivo_nome && (
                  <Button size="sm" variant="ghost" onClick={async () => {
                    try {
                      const r = await axios.get(`${API}/api/pj-pagamentos/${editDialog.id}/nf`,
                        { ...authHeader(), responseType: 'blob' });
                      const url = URL.createObjectURL(new Blob([r.data]));
                      const a = document.createElement('a');
                      a.href = url; a.download = editDialog.nf_arquivo_nome;
                      a.click(); URL.revokeObjectURL(url);
                    } catch (err) { toast.error('Erro ao baixar NF'); }
                  }}>
                    <FileDown className="h-4 w-4 mr-1" /> {editDialog.nf_arquivo_nome}
                  </Button>
                )}
              </div>
            </div>
            <div>
              <Label>Data Pagamento</Label>
              <Input type="date" value={editForm.data_pagamento} onChange={(e) => setEditForm({ ...editForm, data_pagamento: e.target.value })} />
            </div>
            <div>
              <Label>Descrição</Label>
              <Input value={editForm.descricao} onChange={(e) => setEditForm({ ...editForm, descricao: e.target.value })} />
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea value={editForm.observacoes} onChange={(e) => setEditForm({ ...editForm, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>Cancelar</Button>
            <Button onClick={saveEdit} disabled={savingEdit}>
              {savingEdit ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvando...</> : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Adicionar/Editar Item */}
      <Dialog open={!!itemDialog} onOpenChange={(o) => { if (!o) { setItemDialog(false); setItemEditId(null); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{itemEditId ? 'Editar Item' : `Adicionar ${itemForm.tipo === 'provento' ? 'Provento' : 'Desconto'}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Descrição *</Label>
              <Input value={itemForm.descricao} onChange={(e) => setItemForm({ ...itemForm, descricao: e.target.value })} placeholder="Ex: Horas extras, bonificação, desconto..." />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={itemForm.tipo} onValueChange={(v) => setItemForm({ ...itemForm, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="provento">Provento</SelectItem>
                  <SelectItem value="desconto">Desconto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Valor *</Label>
              <CurrencyInput value={itemForm.valor} onChange={(v) => setItemForm({ ...itemForm, valor: v })} placeholder="0,00" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setItemDialog(false); setItemEditId(null); }}>Cancelar</Button>
            <Button onClick={saveItem} disabled={savingItem}>
              {savingItem ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvando...</> : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Gerar Pagamentos (solicitar data) */}
      <Dialog open={showPagamentoDialog} onOpenChange={setShowPagamentoDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Gerar Pagamentos — {MESES[parseInt(mes) - 1]}/{ano}</DialogTitle>
            <DialogDescription>
              Informe a data de pagamento. Os adiantamentos já existentes para esta competência serão deduzidos automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Data de Pagamento *</Label>
              <Input type="date" value={pagamentoData}
                onChange={(e) => setPagamentoData(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPagamentoDialog(false)}>Cancelar</Button>
            <Button onClick={() => gerarPagamentos(pagamentoData)} disabled={generating || !pagamentoData}>
              {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Gerando...</> : 'Gerar Pagamentos'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Gerar Adiantamentos */}
      <Dialog open={showAdiantamentoDialog} onOpenChange={setShowAdiantamentoDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Gerar Adiantamentos — {MESES[parseInt(mes) - 1]}/{ano}</DialogTitle>
            <DialogDescription>
              Gera adiantamento salarial para todos os PJs ativos. O valor será descontado no pagamento mensal.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-slate-600">
            Gera adiantamento salarial para todos os PJs ativos usando <strong>{(adiantamentoPct * 100).toFixed(0)}%</strong> do salário base. O valor será descontado no pagamento mensal.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdiantamentoDialog(false)}>Cancelar</Button>
            <Button onClick={gerarAdiantamentos} disabled={generatingAdiantamento}>
              {generatingAdiantamento ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Gerando...</> : 'Gerar Adiantamentos'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirmar atualização de adiantamentos */}
      <Dialog open={showConfirmAdiantamentoDialog} onOpenChange={setShowConfirmAdiantamentoDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Adiantamento já existente</DialogTitle>
            <DialogDescription>
              Já existe(m) {confirmAdiantamentoData.count} adiantamento(s) para {MESES[parseInt(mes) - 1]}/{ano}.
              Deseja confirmar a atualização com o novo percentual ({confirmAdiantamentoData.pctDisplay}%)?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmAdiantamentoDialog(false)}>Cancelar</Button>
            <Button onClick={confirmarGerarAdiantamentos}>Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PjPagamentosTab;
